import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Download, Loader2, Music, Search } from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_BATCH_ITEMS = 50;
const FORMATS = [
  { value: "mp3", label: "MP3" },
  { value: "wav", label: "WAV" },
  { value: "m4a", label: "M4A" },
  { value: "flac", label: "FLAC" },
];

function fmtDuration(secs) {
  if (!secs) return "—";
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function filenameFromDisposition(headers, fallback) {
  const disposition = headers?.["content-disposition"] || "";
  const match = disposition.match(/filename="?([^";]+)"?/i);
  return match?.[1] || fallback;
}

function triggerBlobDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

async function blobErrorDetail(error, fallback) {
  try {
    const text = await error.response?.data?.text?.();
    return text ? JSON.parse(text).detail || fallback : fallback;
  } catch {
    return fallback;
  }
}

const SongDownloadPage = () => {
  const [query, setQuery] = useState("");
  const [format, setFormat] = useState("mp3");
  const [songs, setSongs] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [downloadingKey, setDownloadingKey] = useState(null);
  const [batchJob, setBatchJob] = useState(null);
  const pollTimerRef = useRef(null);

  const songKey = (song) => song.youtube_video_id || song.spotify_track_id || song.title;

  const runSearch = async () => {
    if (!query.trim()) {
      toast.error("Ingresa el nombre de una canción o artista");
      return;
    }
    setSearching(true);
    setSearchError("");
    setSelected(new Set());
    try {
      const response = await axios.get(`${API_URL}/songs/search`, {
        params: { query: query.trim(), max_results: 50 },
        timeout: 35000,
      });
      if (response.data.setup_required) {
        setSearchError(
          response.data.warnings?.[0] || "La búsqueda no está disponible en este momento."
        );
        setSongs([]);
        return;
      }
      setSongs(response.data.songs || []);
      if ((response.data.songs || []).length === 0) {
        setSearchError("No se encontraron canciones para esa búsqueda.");
      }
    } catch (error) {
      setSearchError(error.response?.data?.detail || "Error en la búsqueda");
      setSongs([]);
    } finally {
      setSearching(false);
    }
  };

  const toggleSelected = (song) => {
    const key = songKey(song);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        if (next.size >= MAX_BATCH_ITEMS) {
          toast.error(`Máximo ${MAX_BATCH_ITEMS} canciones por descarga`);
          return prev;
        }
        next.add(key);
      }
      return next;
    });
  };

  const downloadableSongs = useMemo(() => songs.filter((s) => s.downloadable), [songs]);
  const allSelected =
    downloadableSongs.length > 0 && downloadableSongs.every((s) => selected.has(songKey(s)));

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(downloadableSongs.slice(0, MAX_BATCH_ITEMS).map(songKey)));
    }
  };

  const downloadOne = async (song) => {
    const key = songKey(song);
    setDownloadingKey(key);
    try {
      const response = await axios.get(`${API_URL}/songs/download`, {
        params: { youtube_url: song.youtube_url, title: song.title, format },
        responseType: "blob",
      });
      const blob = new Blob([response.data]);
      triggerBlobDownload(
        blob,
        filenameFromDisposition(response.headers, `${song.title}.${format}`)
      );
      toast.success(`"${song.title}" descargada`);
    } catch (error) {
      toast.error(await blobErrorDetail(error, "No se pudo descargar la canción"));
    } finally {
      setDownloadingKey(null);
    }
  };

  const stopPolling = () => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  const fetchBatchFile = useCallback(async (jobId, totalItems) => {
    try {
      const response = await axios.get(`${API_URL}/songs/download-batch/${jobId}/file`, {
        responseType: "blob",
      });
      const blob = new Blob([response.data], { type: "application/zip" });
      triggerBlobDownload(blob, filenameFromDisposition(response.headers, "descargas.zip"));
      const successCount = response.headers?.["x-successful-count"];
      toast.success(
        successCount ? `${successCount}/${totalItems} canciones descargadas` : "Descarga completada"
      );
    } catch (error) {
      toast.error(await blobErrorDetail(error, "No se pudo descargar el ZIP"));
    } finally {
      setBatchJob(null);
    }
  }, []);

  useEffect(() => {
    if (!batchJob?.job_id || batchJob.status !== "processing") return undefined;

    pollTimerRef.current = setInterval(async () => {
      try {
        const response = await axios.get(`${API_URL}/songs/download-batch/${batchJob.job_id}`);
        const data = response.data;
        setBatchJob((prev) => (prev ? { ...prev, ...data } : prev));
        if (data.status === "completed") {
          stopPolling();
          await fetchBatchFile(batchJob.job_id, data.total_items);
        } else if (data.status === "failed") {
          stopPolling();
          toast.error(data.error || "La descarga del lote falló");
          setBatchJob(null);
        }
      } catch (error) {
        stopPolling();
        toast.error("Error consultando el progreso de la descarga");
        setBatchJob(null);
      }
    }, 2000);

    return () => stopPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchJob?.job_id, batchJob?.status, fetchBatchFile]);

  const startBatchDownload = async () => {
    const items = downloadableSongs
      .filter((s) => selected.has(songKey(s)))
      .map((s) => ({ title: s.title, youtube_url: s.youtube_url }));
    if (items.length === 0) {
      toast.error("Selecciona al menos una canción");
      return;
    }
    try {
      const response = await axios.post(`${API_URL}/songs/download-batch`, { items, format });
      setBatchJob({
        job_id: response.data.job_id,
        total_items: response.data.total_items,
        completed_items: 0,
        successful_items: 0,
        status: "processing",
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "No se pudo iniciar la descarga");
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b]">
      <header className="sticky top-0 z-50 glass-panel">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center gap-4">
          <Link to="/" className="text-gray-400 hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <h1 className="text-white font-semibold">Buscar y Descargar Canciones</h1>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        <Card className="bg-[#18181b] border-white/10">
          <CardHeader>
            <CardTitle className="text-white">Buscar por canción o artista</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col sm:flex-row gap-3">
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => event.key === "Enter" && runSearch()}
              placeholder="Nombre de la canción o del artista"
              className="bg-black/30 border-white/10 text-white flex-1"
              disabled={searching}
            />
            <Select value={format} onValueChange={setFormat}>
              <SelectTrigger className="w-full sm:w-28 bg-black/30 border-white/10 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#18181b] border-white/10">
                {FORMATS.map((f) => (
                  <SelectItem key={f.value} value={f.value} className="text-white">
                    {f.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={runSearch} disabled={searching} className="bg-primary hover:bg-primary/90 gap-2">
              {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              Buscar
            </Button>
          </CardContent>
        </Card>

        {searchError && (
          <Card className="bg-[#18181b] border-red-500/30">
            <CardContent className="py-4">
              <p className="text-red-400 text-sm">{searchError}</p>
            </CardContent>
          </Card>
        )}

        {songs.length > 0 && (
          <Card className="bg-[#18181b] border-white/10">
            <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-3">
              <CardTitle className="text-white">
                {songs.length} resultado{songs.length === 1 ? "" : "s"}
              </CardTitle>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                  <Checkbox checked={allSelected} onCheckedChange={toggleSelectAll} />
                  Seleccionar todo (máx. {MAX_BATCH_ITEMS})
                </label>
                <Button
                  onClick={startBatchDownload}
                  disabled={selected.size === 0 || !!batchJob}
                  className="bg-primary hover:bg-primary/90 gap-2"
                >
                  {batchJob ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  Descargar seleccionadas ({selected.size})
                </Button>
              </div>
            </CardHeader>
            {batchJob && (
              <CardContent className="pb-0">
                <div className="space-y-2">
                  <p className="text-sm text-gray-300">
                    Descargando {batchJob.completed_items || 0}/{batchJob.total_items} ·{" "}
                    {batchJob.successful_items || 0} exitosas
                  </p>
                  <Progress
                    value={
                      batchJob.total_items
                        ? ((batchJob.completed_items || 0) / batchJob.total_items) * 100
                        : 0
                    }
                    className="h-2"
                  />
                </div>
              </CardContent>
            )}
            <CardContent className="space-y-2">
              {songs.map((song) => {
                const key = songKey(song);
                return (
                  <div
                    key={key}
                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 border border-white/5"
                  >
                    <Checkbox
                      checked={selected.has(key)}
                      onCheckedChange={() => toggleSelected(song)}
                      disabled={!song.downloadable}
                    />
                    {song.thumbnail_url ? (
                      <img src={song.thumbnail_url} alt="" className="w-16 h-9 object-cover rounded" />
                    ) : (
                      <div className="w-16 h-9 rounded bg-black/40 flex items-center justify-center">
                        <Music className="w-4 h-4 text-gray-600" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm truncate">{song.title}</p>
                      <p className="text-xs text-gray-500 truncate">
                        {song.channel || song.artist} · {fmtDuration(song.duration_seconds)}
                      </p>
                    </div>
                    {!song.downloadable && (
                      <Badge className="bg-white/10 text-gray-400 border border-white/10">
                        Sin audio
                      </Badge>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-white/10 text-gray-300 gap-2"
                      disabled={!song.downloadable || downloadingKey === key}
                      onClick={() => downloadOne(song)}
                    >
                      {downloadingKey === key ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4" />
                      )}
                      Descargar
                    </Button>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default SongDownloadPage;
